import { comluxgroup } from '@luxuryescapes/contract-data-event-schemas'
import { type SelfDescribingJson } from '@snowplow/browser-tracker'
import { LabFeatureFlags } from 'constants/lab'
import { isFunnelConfigPayload } from 'lib/lab/dynamicPayload'

/**
 * Maps internal toggle property names to Snowplow feature names (kebab-case per spec)
 */
const TOGGLE_TO_FEATURE_MAP = {
  walledGarden: 'walled-garden',
  roomUpgrade: 'room-upgrade',
  luxPlus: 'luxplus',
  flights: 'flights',
  experiences: 'experiences',
  cfmr: 'cfmr',
  travelProtection: 'travel-protection',
  carHire: 'car-hire',
} as const

type ToggleKey = keyof typeof TOGGLE_TO_FEATURE_MAP

/**
 * Creates the Funnel Rule context with rule ID and name.
 * Returns null if funnel optimiser is not enabled or no data available.
 */
export function funnelRuleContext(state: App.State): SelfDescribingJson | null {
  const funnelFlag = state.system.features.labFlags[LabFeatureFlags.enableFunnelOptimiser]

  if (!funnelFlag?.enabled) {
    return null
  }

  const payload = funnelFlag.value
  if (!isFunnelConfigPayload(payload)) {
    return null
  }

  return comluxgroup.createFunnelRule_1_0_0({
    ruleId: payload.ruleId,
    ruleName: payload.ruleName,
  })
}

/**
 * Creates an array of Funnel Rule Config contexts (one per toggle).
 * Each toggle maps to a feature/state pair for BigQuery analysis.
 */
export function funnelRuleConfigContexts(state: App.State): Array<SelfDescribingJson> {
  const funnelFlag = state.system.features.labFlags[LabFeatureFlags.enableFunnelOptimiser]

  if (!funnelFlag?.enabled) {
    return []
  }

  const payload = funnelFlag.value
  if (!isFunnelConfigPayload(payload)) {
    return []
  }

  const toggles = payload.toggles ?? {}

  return Object.entries(toggles)
    .filter((entry): entry is [ToggleKey, boolean] => entry[0] in TOGGLE_TO_FEATURE_MAP)
    .map(([key, enabled]) =>
      comluxgroup.createFunnelRuleConfig_1_0_0({
        feature: TOGGLE_TO_FEATURE_MAP[key],
        state: enabled ? 'enabled' : 'disabled',
      }),
    )
}
